import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { PROGRAMS, SCHOOL } from '../data/siteData';
import { Section, SectionHeading, Reveal, Icon, Wave, wiggle, float } from './common';

export default function Programs() {
  return (
    <Section id="programs" bg={COLORS.cream} sx={{ pt: { xs: 4, md: 6 } }}>
      <Box sx={{ position: 'absolute', top: 60, left: -50, width: 160, height: 160, borderRadius: '50%', bgcolor: `${COLORS.pink}22`, animation: `${float} 7s ease-in-out infinite` }} />
      <Box sx={{ position: 'absolute', bottom: 140, right: -40, width: 140, height: 140, borderRadius: '30px', transform: 'rotate(20deg)', bgcolor: `${COLORS.blue}22`, animation: `${float} 8s ease-in-out infinite` }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <SectionHeading
          eyebrow="Our Programs"
          title="Fun Learning for Every Age"
          subtitle="Age-friendly programs designed to nurture curiosity, creativity and confidence, one happy step at a time."
          color={COLORS.blue}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3.5 }}>
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.title} direction="up" delay={i * 130} sx={{ height: '100%' }}>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#fff',
                  borderRadius: '32px',
                  overflow: 'hidden',
                  border: `4px solid ${p.color}`,
                  boxShadow: '0 12px 30px rgba(27,42,107,.1)',
                  transition: 'transform .35s ease, box-shadow .35s ease',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 26px 44px ${p.color}55`,
                    '& .prog-icon': { animation: `${wiggle} .7s ease 2` },
                  },
                }}
              >
                <Box sx={{ bgcolor: p.color, color: '#fff', p: 3, pb: 4, position: 'relative' }}>
                  <Box className="prog-icon" sx={{ width: 68, height: 68, borderRadius: '22px', bgcolor: 'rgba(255,255,255,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                    <Icon name={p.icon} sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontSize: 26, lineHeight: 1.1 }}>{p.title}</Typography>
                  <Box sx={{ display: 'inline-block', mt: 1, px: 1.8, py: 0.3, borderRadius: 50, bgcolor: '#fff', color: p.color, fontFamily: FONTS.heading, fontWeight: 800, fontSize: 14 }}>
                    {p.age}
                  </Box>
                  <Box component="svg" viewBox="0 0 400 30" preserveAspectRatio="none" sx={{ position: 'absolute', bottom: -1, left: 0, width: '100%', height: 22 }}>
                    <path d="M0,15 Q50,-5 100,15 T200,15 T300,15 T400,15 L400,30 L0,30 Z" fill="#fff" />
                  </Box>
                </Box>
                <Box sx={{ p: 3, pt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: 15.5, lineHeight: 1.65 }}>{p.text}</Typography>
                  <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: `${p.color}25`, color: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name="check" sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: COLORS.navy }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>

        <Reveal sx={{ textAlign: 'center', mt: 7 }}>
          <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 700, fontSize: 20, color: COLORS.navy, mb: 2 }}>
            Not sure which program fits your child? Talk to us.
          </Typography>
          <Button
            href={`tel:+${SCHOOL.phoneRaw}`}
            variant="contained"
            startIcon={<Icon name="phone" />}
            sx={{ px: 4, py: 1.4, fontSize: 18, bgcolor: COLORS.orange, boxShadow: '0 7px 0 #E8590C', '&:hover': { bgcolor: '#ff9a55', transform: 'translateY(3px)', boxShadow: '0 4px 0 #E8590C' }, transition: 'all .2s ease' }}
          >
            Call {SCHOOL.phone}
          </Button>
        </Reveal>
      </Container>
      <Wave color="#ffffff" sx={{ mt: 6, mb: '-2px' }} />
    </Section>
  );
}
